import React, { useEffect, useState } from 'react';
import { fetchInventoryItems } from 'services/api/inventoryItems';
import { getPageLimit, inventoryItemAddressRangeFormat, showError, updatePageLimit } from 'utils';
import qs from 'query-string';
import DataTable from 'components/DataTable';
import { tableStyles } from '../../../css/tableStyles';
import styles from './index.module.scss';
import { Loading } from '../../../components/loading/Loading';
import ActionButton from 'components/ActionButton';
import Filter from './Filter';
import _ from 'lodash';
import Currency from 'components/Currency/Currency';
import InventoryItemId from 'components/InventoryItemId';
import QuantityGraph from './quantityGraph';
import BtnCellRenderer from 'components/BtnRenderer';
import EditInventoryAddressModal from 'components/EditInventoryAddressModal';

let filters: { [p: string]: any; page: number } = {
  page: 0,
  deleted: false,
};

let sort = {};

const InventoryItems = props => {
  const [loading, setLoading] = useState<boolean>(false);
  const [addressLoading, setAddressLoading] = useState<boolean>(false);
  const [inventoryItems, setInventoryItems] = useState<Record<string, any>>({});
  const [showAddressModal, setShowAddressModal] = useState<boolean>(false);
  const [selectedInventoryItemId,setSelectedInventoryItemId] = useState<string>('');

  const columns = [
    {
      name: 'ItemID',
      selector: 'objectId',
      sortable: false,
      width: '150px',
      // eslint-disable-next-line react/display-name
      cell: row => (
        <InventoryItemId itemDetails={row} />
      ),
    },
    {
      name: 'Quantity Graph',
      width: '200px',
      cell: ({ stockBefore7Days, stock }) => {
        if (stockBefore7Days && stock) {
          return <QuantityGraph data={[[0, stockBefore7Days], [stock, stock]]} />;
        } else {
          return null;
        }
      },
    },
    {
      name: 'Item Name',
      selector: 'name',
      sortable: false,
      width: '150px',
      cell: row => (
        <div>{row?.name}</div>
      ),
    },
    {
      name: 'Category',
      // selector: 'category.description',
      cell: row => (
        <div>{row.category?.description || ''}</div>
      ),
      sortable: false,
    },
    {
      name: 'Price',
      selector: 'price',
      sortable: false,
      // eslint-disable-next-line react/display-name
      format: (row) => (<Currency amount={parseFloat(row.price?.toFixed(2))} default={'-'} />),
    },
    {
      name: 'Cost',
      selector: 'weightedAvgCost',
      sortable: false,
      // eslint-disable-next-line react/display-name
      cell: row => (row.weightedAvgCost ? <Currency amount={parseFloat(row.weightedAvgCost.toFixed(2))} /> : '-'),
    },
    {
      name: 'Margin',
      selector: 'weightedAvgCost',
      sortable: false,
      // eslint-disable-next-line react/display-name
      cell: row => (row.weightedAvgCost ? <Currency amount={parseFloat((row.price - row.weightedAvgCost).toFixed(2))} /> : '-'),
    },
    {
      name: 'Margin %',
      selector: 'weightedAvgCost',
      sortable: false,
      // eslint-disable-next-line react/display-name
      cell: row => {
        if (row.weightedAvgCost) {
          const marginPercent = (row.price - row.weightedAvgCost) * 100 / row.weightedAvgCost;
          return <div>{marginPercent.toFixed(2)}</div>;
        } else {
          return '-';
        }
      },
    },
    {
      name: 'Quantity',
      selector: 'stock',
      sortable: false,
    },
    {
      name: 'Probability',
      selector: 'probability',
      sortable: true,
      // eslint-disable-next-line react/display-name
      format: row => _.isNil(row.probability) ? <div>{'N/A'}</div> : <div>{row.probability}</div>,
    },
    {
      name: 'Unfulfilled Stock',
      selector: 'unfulfilledStock',
      sortable: false,
      format: row => row.unfulfilledStock ? row.unfulfilledStock : '0',
    },
    {
      name: 'Unit',
      selector: 'unit',
      sortable: false,
      // eslint-disable-next-line react/display-name
      cell: row => (
        <div>{row.unit?.description || ''}</div>
      ),
    },
    {
      name: 'Active',
      selector: 'active',
      sortable: false,
      // eslint-disable-next-line react/display-name
      cell: row => {
        if (typeof row.active !== 'undefined') {
          return <div>{row.active.toString()}</div>;
        } else {
          return <div>{'N/A'}</div>;
        }
      },
    },
    {
      name: 'Origin',
      selector: 'origin',
      sortable: false,
      // eslint-disable-next-line react/display-name
      cell: row => (
        <div>{row.origin || 'None'}</div>
      ),
    },
    {
      name: 'Promotional',
      selector: 'isPromotional',
      sortable: false,
      // eslint-disable-next-line react/display-name
      cell: row => {
        if (typeof row.isPromotional !== 'undefined') {
          return <div>{row.isPromotional.toString()}</div>;
        } else {
          return <div>{'N/A'}</div>;
        }
      },
    },
    {
      name: 'Featured',
      selector: 'isFeatured',
      sortable: false,
      // eslint-disable-next-line react/display-name
      cell: row => {
        if (typeof row.isFeatured !== 'undefined') {
          return <div>{row.isFeatured.toString()}</div>;
        } else {
          return <div>{'N/A'}</div>;
        }
      },
    },
    {
      name: 'Must Have',
      selector: 'mustHave',
      sortable: false,
      // eslint-disable-next-line react/display-name
      cell: row => {
        if (typeof row.mustHave !== 'undefined') {
          return <div>{row.mustHave.toString()}</div>;
        } else {
          return <div>{'N/A'}</div>;
        }
      },
    },
    {
      name: 'Inventory Address',
      selector: 'inventoryAddress',
      sortable: true,
      format: row => {
        if(doesInventoryAddressExists(row)){
          return  row?.inventoryAddress ? inventoryItemAddressRangeFormat(row.inventoryAddress) : '-';
        } else {
          return <BtnCellRenderer text={'Add Address'} clicked={()=>showItemAddressEditModal(row.objectId)} />;
        }
      },
      width: '150px',
    },
  ];

  function showItemAddressEditModal (inventoryId: string){
    setSelectedInventoryItemId(inventoryId);
    setShowAddressModal(true);
  }

  function doesInventoryAddressExists (row: any){
    if(!row?.inventoryAddress || inventoryItemAddressRangeFormat(row.inventoryAddress)==='N/A'){
      return false;
    } else {
      return true;
    }
  }
  
  const fetchInventoryItemsFn = async () => {
    setSelectedInventoryItemId('');
    setLoading(true);
    const { page, categoryIds, ...rest } = filters;
    try {
      const response = await fetchInventoryItems({
        page,
        limit: getPageLimit(),
        filter: { ...rest, categoryIds: categoryIds?.split('|'), quantityRange: { min: filters.min, max: filters.max }},
        sort: { ...sort },
      });
      setInventoryItems(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const queryStringToFilter = () => {
    return qs.parse(props.location.search, { parseBooleans: true, parseNumbers: true });
  };

  useEffect(() => {
    filters = {
      ...filters,
      ...queryStringToFilter(),
    };
    sort = {};
    fetchInventoryItemsFn();
  }, []);

  const filterToQueryString = () => {
    return qs.stringify({ ...filters });
  };

  const updateQueryString = () => {
    const qs = filterToQueryString();
    props.history.push({
      pathname: props.location.pathname,
      search: qs,
    });
  };

  const onChangePage = (page: number) => {
    filters.page = page - 1;
    updateQueryString();
    fetchInventoryItemsFn();
  };

  const onChangeRowsPerPage = (rowsPerPage: number, currentPage: number) => {
    filters.limit = rowsPerPage;
    updatePageLimit(rowsPerPage);
    onChangePage(currentPage);
  };

  const onFilterSubmit = (updatedFilter: Record<string, any>) => {
    if (updatedFilter.itemId) {
      props.history.push(`/inventory-items/${updatedFilter.itemId}`);
    } else {
      filters = { ...updatedFilter, page: 0 };
      updateQueryString();
      fetchInventoryItemsFn();
    }
  };

  const onSort = (column: Record<string, any>, sortDirection: string) => {
    sort = {
      ...sort,
      [column.selector]: sortDirection,
    };
    fetchInventoryItemsFn();
  };
  return (
    <>
      {/* {loading ? <Loading /> : null} */}
      <div className={styles['header']}>
        <div className={styles['heading']}>Inventory Items</div>
        <ActionButton
          text={<>Inventory Item <span>+</span></>}
          active={true}
          isLoading={false}
          onClick={() => props.history.push('/inventory-items/create')}
        />
      </div>
      <Filter {...props} onSubmit={onFilterSubmit} />
      <DataTable
        keyField="objectid"
        columns={columns}
        noHeader={true}
        data={inventoryItems.items}
        highlightOnHover={true}
        customStyles={tableStyles}
        pagination={true}
        paginationServer={true}
        paginationPerPage={getPageLimit()}
        paginationDefaultPage={(filters.page || 0) + 1}
        paginationTotalRows={inventoryItems.total}
        paginationRowsPerPageOptions={[50, 100, 200]}
        onChangePage={onChangePage}
        onChangeRowsPerPage={onChangeRowsPerPage}
        onSort={onSort}
        loading={loading}
      />
      {showAddressModal && 
      <EditInventoryAddressModal
        onCancel={() => {
          setShowAddressModal(false);
        }}
        setLoading={setAddressLoading}
        loading={addressLoading}
        onSuccess={()=> {
          setShowAddressModal(false);
          fetchInventoryItemsFn();
        }}
        inventoryId={selectedInventoryItemId}
      />}
    </>
  );
};

export { InventoryItems };
